import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import LegalAckModal from '@/components/LegalAckModal';
import { useAuth } from '@/context/AuthContext';
import { ShieldAlert, Check } from 'lucide-react';

const SCALE = [
  { v: 0, label: "Not at all" },
  { v: 1, label: "Several days" },
  { v: 2, label: "More than half" },
  { v: 3, label: "Nearly every day" },
];

const ETHNICITIES = [
  { id: 'black', label: 'Black / African American' },
  { id: 'latino', label: 'Latino / Hispanic' },
  { id: 'asian', label: 'Asian' },
  { id: 'mena', label: 'Middle Eastern / North African' },
  { id: 'indigenous', label: 'Indigenous / Native' },
  { id: 'white', label: 'White' },
  { id: 'multiracial', label: 'Multiracial' },
  { id: 'other', label: 'Another background' },
  { id: 'prefer_not', label: 'Prefer not to say' },
];
const ASSESS_YEAR = new Date().getFullYear();
const ASSESS_YEARS = Array.from({ length: 101 }, (_, k) => ASSESS_YEAR - k);
const ASSESS_PROFILE_KEY = 'bp_assessment_profile';

export default function Assessment() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [assess, setAssess] = useState(null);
  const [flat, setFlat] = useState([]);
  const [kind, setKind] = useState('baseline'); // baseline | monthly — drives timeframe wording
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  // Pre-assessment "about you" intro — only for new (logged-out) users. Logged-in
  // members already provided this, so they skip straight to the questions.
  const [started, setStarted] = useState(false);
  const [birthYear, setBirthYear] = useState('');
  const [ageOk, setAgeOk] = useState(null);
  const [ethnicity, setEthnicity] = useState('');

  useEffect(() => { if (user) setStarted(true); }, [user]);

  useEffect(() => {
    http.get('/assessment/questions').then(({data}) => {
      setAssess(data);
      const list = [];
      data.domains.forEach(d => d.questions.forEach(q => list.push({...q, domain: d.name})));
      setFlat(list);
    });
    // Know whether this is the baseline or a monthly progress check-in so the
    // timeframe wording matches the cadence. Falls back to baseline (e.g. onboarding).
    http.get('/assessment/status')
      .then(({data}) => { if (data?.next_kind) setKind(data.next_kind); })
      .catch(() => {});
  }, []);

  if (!assess) return <div className="min-h-screen flex items-center justify-center text-navy/60">Loading…</div>;

  // ── Pre-assessment intro: 18+ confirmation + optional race/ethnicity (tailoring)
  const beginAssessment = () => {
    const y = parseInt(birthYear, 10);
    const ok = !!y && (ASSESS_YEAR - y) >= 18;
    setAgeOk(ok);
    if (!ok) return;
    try {
      sessionStorage.setItem(ASSESS_PROFILE_KEY, JSON.stringify({
        birth_year: y,
        ethnicity: ETHNICITIES.find(e => e.id === ethnicity)?.label || null,
      }));
    } catch { /* ignore */ }
    setStarted(true);
  };

  if (!started) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] p-6 flex items-center">
        <div className="w-full max-w-md lg:max-w-lg mx-auto bg-white rounded-3xl p-8 lg:p-10 card-soft animate-fade-up" data-testid="assessment-intro">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3">Before we begin</p>
          <h1 className="font-heading text-3xl mb-2">A little about you</h1>
          <p className="text-navy/60 text-sm mb-6">This helps us tailor your coaching and content from the very first step.</p>

          <label className="block mb-5">
            <span className="text-xs uppercase tracking-widest text-navy/60">Year of birth</span>
            <select
              data-testid="assessment-birth-year"
              value={birthYear}
              onChange={(e) => { setBirthYear(e.target.value); setAgeOk(null); }}
              className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="">Select a year…</option>
              {ASSESS_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span className="mt-1 block text-[11px] text-navy/45">Brotherhood+ is an 18+ experience.</span>
          </label>

          {ageOk === false && (
            <div className="mb-5 flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-100 text-red-800 text-sm" data-testid="assessment-age-fail">
              <ShieldAlert size={16} className="mt-0.5 shrink-0"/>
              <span>You must be at least 18 to use Brotherhood+. If you&apos;re under 18, please talk to a trusted adult — you can also call or text <b>988</b> (US) any time.</span>
            </div>
          )}

          <p className="text-xs uppercase tracking-widest text-navy/60 mb-1">Race / ethnicity <span className="normal-case tracking-normal text-navy/40">(optional)</span></p>
          <div className="flex flex-wrap gap-2 mb-3">
            {ETHNICITIES.map(e => (
              <button
                key={e.id}
                type="button"
                data-testid={`assessment-ethnicity-${e.id}`}
                onClick={() => setEthnicity(ethnicity === e.id ? '' : e.id)}
                className={`text-xs px-3 py-2 rounded-full border transition-smooth ${
                  ethnicity === e.id ? 'bg-navy text-white border-navy' : 'bg-beige border-transparent hover:bg-[#EAE5D9]'
                }`}>
                <span className="flex items-center gap-1.5">
                  {ethnicity === e.id && <Check size={12}/>}{e.label}
                </span>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-navy/45 leading-relaxed mb-6">
            Optional. We use this only to tailor your coaching and content — it&apos;s private, never shared, and you can skip it.
          </p>

          <button
            data-testid="assessment-begin"
            onClick={beginAssessment}
            disabled={!birthYear}
            className="w-full bg-navy text-white rounded-full py-4 font-medium hover:bg-navy-soft transition-smooth disabled:opacity-40">
            Start the Wellness Check-In
          </button>
          <p className="text-xs text-navy/50 mt-4 text-center">Brotherhood+ is not therapy and does not diagnose.</p>
        </div>
      </div>
    );
  }


  const isMonthly = kind === 'monthly';
  const timeframeLead = isMonthly ? 'Over the past month:' : 'Over the past two weeks:';
  const headerLabel = isMonthly ? 'Monthly Progress Check-In' : 'Baseline Wellness Check-In';

  const q = flat[i];
  const answered = answers[q?.id] !== undefined;
  const progress = ((i + (answered ? 1 : 0)) / flat.length) * 100;

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const { data } = await http.post('/assessment/submit', { answers: finalAnswers });
      sessionStorage.setItem('bp_last_result', JSON.stringify(data));
      nav('/assessment/results');
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      if (ex?.response?.status === 409 && detail?.error === 'assessment_locked') {
        toast.info(detail.message || 'Come back next month for your progress assessment.', { duration: 5200 });
        setTimeout(() => nav('/app'), 900);
      } else {
        toast.error('Could not submit');
      }
      setSubmitting(false);
      setAdvancing(false);
    }
  };

  // Selecting an answer records it and auto-advances (no Next button needed).
  // A short delay lets the selection highlight register before moving on.
  const pick = (v) => {
    if (advancing || submitting) return;
    const next = { ...answers, [q.id]: v };
    setAnswers(next);
    setAdvancing(true);
    setTimeout(() => {
      if (i < flat.length - 1) {
        setI(i + 1);
        setAdvancing(false);
      } else {
        submit(next);
      }
    }, 260);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-6 flex items-center">
      <LegalAckModal
        storageKey="bp_ack_assessment_v1"
        title="Before this wellness assessment"
        body="These assessments are designed for self-reflection and are not intended to diagnose any medical or mental health condition."
        readMoreTo="/app/profile/trust-safety?section=wellness"
        readMoreLabel="Read the Wellness Disclaimer"
      />
      <div className="w-full max-w-md lg:max-w-2xl mx-auto">
        <div className="mb-4 flex items-center justify-between text-xs lg:text-sm text-navy/50">
          <span>{headerLabel}</span>
          <span>{i+1} of {flat.length}</span>
        </div>
        <div className="h-1 bg-navy/10 rounded-full overflow-hidden mb-8">
          <div className="h-full bg-gold transition-all" style={{width: `${progress}%`}}/>
        </div>
        <div className="bg-white rounded-3xl p-8 lg:p-12 card-soft animate-fade-up">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4">{q.domain}</p>
          <h2 className="font-heading text-2xl lg:text-3xl leading-snug mb-2">{timeframeLead}</h2>
          <p className="text-lg lg:text-xl text-navy/80 mb-8 lg:mb-10">{q.text}</p>
          <div className="space-y-2 lg:space-y-3">
            {SCALE.map(s => (
              <button key={s.v} data-testid={`assessment-answer-${q.id}-${s.v}`} onClick={()=>pick(s.v)} disabled={submitting || advancing}
                className={`w-full text-left p-4 lg:p-5 rounded-2xl border transition-smooth text-sm lg:text-base ${
                  answers[q.id]===s.v ? 'bg-navy text-white border-navy' : 'bg-beige border-transparent hover:bg-[#EAE5D9]'
                }`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-between gap-3">
            {i > 0 ? (
              <button data-testid="assessment-back" onClick={()=>{ if (!advancing && !submitting) setI(i-1); }} className="text-sm text-navy/50 hover:text-navy px-2 py-3">← Back</button>
            ) : <span />}
            {submitting
              ? <span className="text-sm text-navy/50" data-testid="assessment-scoring">Scoring…</span>
              : <span className="text-xs text-navy/40">Tap an answer to continue</span>}
          </div>
        </div>
        <button
          data-testid="assessment-exit"
          onClick={() => nav('/login')}
          className="block mx-auto text-xs text-navy/40 hover:text-navy mt-6 underline underline-offset-4"
        >
          Exit to sign in
        </button>
        <p className="text-xs text-navy/50 mt-4 text-center">Brotherhood+ is not therapy and does not diagnose.</p>
      </div>
    </div>
  );
}
